import { Component, computed, effect, signal } from '@angular/core'
import { MapGeoJSONFeature } from 'maplibre-gl'
import { format } from 'date-fns'

// Services
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { AppStateService } from 'src/app/core/services/app-state.service'

// Models and Types
import { DetailsModel } from 'src/app/shared/models/details.model'
import { WarningDataModel } from 'src/app/shared/models/warning-event.model'

// Enums and Constants
import { Layers } from 'src/app/shared/layers-config/layers'
import { CustomerMetrics } from '../../enums/customer-metrics.enum'
import { Permissions } from 'src/app/core/enums/permissions.enum'
import { AppPaths } from 'src/app/app-routing.module'

// Utils
import { getTimeZoneLabel } from 'src/app/shared/utils/date.utils'
import { getEmailLink } from '../../utils/customer-warning-details-utils'

// Constants
const CUSTOMER_WARNING_LAYERS = ['customer-lines', 'customer-hexagons'] as const
const SIMULATION_WARNING_LAYERS = ['simw-lines', 'simw-hexagons'] as const

@Component({
  selector: 'app-customer-warnings-details',
  templateUrl: './customer-warnings-details.component.html',
  styleUrl: './customer-warnings-details.component.scss',
  standalone: false,
})
export class CustomerWarningsDetailsComponent {
  // Public properties for template
  public readonly CustomerMetrics = CustomerMetrics
  public readonly Permissions = Permissions

  // Feature selection state
  public selectedFeatureId = ''
  public selectedFeatureIds: string[] = []
  public features: MapGeoJSONFeature[] = []
  public index = 0

  // UI state
  private readonly _details = signal<Map<string, DetailsModel>>(new Map<string, DetailsModel>())
  public readonly details = this._details.asReadonly()
  public hasDetails = computed(() => this.details().size > 0)
  public rawFeature: string | undefined
  public zoomLevel: number | undefined
  public mailToLink = ''

  constructor(
    private readonly mapEventService: MapEventsService,
    public readonly appState: AppStateService,
  ) {
    this.initialize()
  }

  // Public Methods
  public toggleFeature(index: number): void {
    if (index >= 0 && index < this.features.length) {
      this.index = index
      this.updateDetails()
    }
  }

  public close(): void {
    this.resetState()
    this.updateAppState()
  }

  // Private Initialization Methods
  private initialize(): void {
    this._details.set(new Map<string, DetailsModel>())
    this.setupFeatureClickSubscription()
  }

  private setupFeatureClickSubscription(): void {
    effect(() => {
      this.handleFeatureClick(this.mapEventService.clickedFeatures())
    })
  }

  // Event Handlers
  private handleFeatureClick(features: MapGeoJSONFeature[]): void {
    if (!this.isValidFeatureClick(features)) return

    this._details.set(new Map<string, DetailsModel>())
    this.updateIndex(features)

    if (this.isFeatureAlreadySelected(features)) {
      this.close()
      return
    }

    this.features = features
    this.updateDetails()
  }

  // Feature Selection Logic
  private isValidFeatureClick(features: MapGeoJSONFeature[]): boolean {
    if (!features?.length) return false

    const firstFeature = features[0]
    return (
      this.isCustomerWarningLayer(firstFeature.layer?.id) ||
      this.isSimulationWarningLayer(firstFeature.layer?.id)
    )
  }

  private isCustomerWarningLayer(layerId: string | undefined): boolean {
    return CUSTOMER_WARNING_LAYERS.includes(layerId as any)
  }

  private isSimulationWarningLayer(layerId: string | undefined): boolean {
    return SIMULATION_WARNING_LAYERS.includes(layerId as any)
  }

  private updateIndex(features: MapGeoJSONFeature[]): void {
    this.index = this.features.length !== features.length ? 0 : this.index
  }

  private isFeatureAlreadySelected(features: MapGeoJSONFeature[]): boolean {
    return !!(
      this.selectedFeatureId && this.selectedFeatureId === features[this.index]?.id?.toString()
    )
  }

  // Details Management
  private updateDetails(): void {
    if (!this.features[this.index]) return

    const currentFeature = this.features[this.index]

    this.updateFeatureSelection(currentFeature)
    this.updateZoomLevel()
    this.updateMailLink(currentFeature)

    const properties = this.extractProperties(currentFeature)
    const filteredProperties = this.createFilteredProperties(properties)

    this.updateRawFeature(currentFeature, filteredProperties)
    this.updateDetailsModel(properties)
    this.updateAppState()
  }

  private updateFeatureSelection(currentFeature: MapGeoJSONFeature): void {
    this.selectedFeatureId = currentFeature.id?.toString() ?? ''
    // Cache keys from the currently visible detail list for the next refresh cycle.
    this.selectedFeatureIds = this.extractAllFeatureIds()
  }

  private extractAllFeatureIds(): string[] {
    return this.features
      .map((feature) => this.getFeatureSelectionKey(feature))
      .filter((id): id is string => !!id)
  }

  private getFeatureSelectionKey(feature: MapGeoJSONFeature): string {
    // Prefer stable road/area identifiers over map feature.id, which can change between minutes.
    const segmentId = feature.properties?.['segmentId']
    if (segmentId !== undefined && segmentId !== null && segmentId !== '') {
      return `segment-${String(segmentId)}`
    }

    const h3id = feature.properties?.['h3id']
    if (h3id) {
      return `h3-${String(h3id)}`
    }

    return this.createFeatureUniqueKey(feature)
  }

  private updateZoomLevel(): void {
    this.zoomLevel = this.appState.getState().zoom
  }

  private updateMailLink(feature: MapGeoJSONFeature): void {
    this.mailToLink = getEmailLink(feature)
  }

  private extractProperties(feature: MapGeoJSONFeature): WarningDataModel {
    return feature.properties as WarningDataModel
  }

  private createFilteredProperties(properties: WarningDataModel): any {
    const baseProperties = {
      name: properties.name,
      segmentId: properties.segmentId,
      startTime: properties.startTime,
      expiryTime: properties.expiryTime,
      eventCoordinates: properties.eventCoordinates,
      countryCode: properties.countryCode,
      roadClass: properties.roadClass,
      bidirectional: properties.bidirectional,
      direction: properties.direction,
      isCancelled: properties.isCancelled,
      probability: properties.probability,
      h3id: properties.h3id,
      confidence: properties.confidence,
      warningUUID: properties.warningUUID,
      dfrs: properties.dfrs,
    }

    return {
      ...baseProperties,
      ...this.addVersionIfExists(properties),
      ...this.addWarningCreatedIfExists(properties),
    }
  }

  private addVersionIfExists(properties: WarningDataModel): object {
    return properties.version ? { version: properties.version } : {}
  }

  private updateRawFeature(feature: MapGeoJSONFeature, filteredProperties: any): void {
    const customerView = {
      type: feature.type,
      geometry: feature.geometry,
      properties: filteredProperties,
    }
    this.rawFeature = JSON.stringify(customerView, undefined, 2)
  }

  private updateDetailsModel(properties: WarningDataModel): void {
    const detailsItems = this.createDetailsItems(properties)
    this.addWarningTypeSpecificDetails(detailsItems, properties)

    this.setDetail('RawDetails', {
      title: 'Hazards',
      items: detailsItems,
    })
  }

  private setDetail(key: string, value: DetailsModel): void {
    this._details.update((current) => {
      const next = new Map(current)
      next.set(key, value)
      return next
    })
  }

  private createDetailsItems(properties: WarningDataModel): any[] {
    const baseItems = [
      { description: 'Global ID', value: this.selectedFeatureId },
      { description: 'Warning UUID', value: properties.warningUUID },
      { description: 'Type', value: properties.name },
      { description: 'Direction', value: properties.direction },
      {
        description: this.getValidTimeLabel(properties),
        value: this.formatValidTimeValue(properties),
      },
      { description: 'isCancelled', value: properties.isCancelled.toString() },
    ]

    return [
      ...baseItems,
      ...this.addWarningCreatedIfExists(properties),
      ...this.addVersionDetailsIfExists(properties),
      ...this.addConfidenceIfExists(properties),
      ...this.addDfrsIfExists(properties),
      ...this.addTargetIfExists(properties),
    ]
  }

  private addWarningCreatedIfExists(properties: WarningDataModel): any[] {
    return properties.creationTime !== undefined && properties.creationTime !== null
      ? [
          {
            description: 'Warning created',
            value: format(new Date(properties.creationTime), 'yyyy-MM-dd HH:mm:ss'),
          },
        ]
      : []
  }

  private getValidTimeLabel(properties: WarningDataModel): string {
    const timezone = getTimeZoneLabel(new Date(properties.startTime))
    return `Valid time (${timezone})`
  }

  private formatValidTimeValue(properties: WarningDataModel): string {
    const startTime = properties.startTime
      ? format(new Date(properties.startTime), 'yyyy-MM-dd HH:mm:ss')
      : '-'
    const endTime = properties.expiryTime
      ? format(new Date(properties.expiryTime), 'yyyy-MM-dd HH:mm:ss')
      : '-'
    return `${startTime} - ${endTime}`
  }

  private addVersionDetailsIfExists(properties: WarningDataModel): any[] {
    return properties.version !== undefined && properties.version !== null
      ? [{ description: 'Version', value: properties.version.toString() }]
      : []
  }

  private addConfidenceIfExists(properties: WarningDataModel): any[] {
    return properties.confidence
      ? [{ description: 'Confidence', value: properties.confidence.toString() }]
      : []
  }

  private addDfrsIfExists(properties: WarningDataModel): any[] {
    return properties.dfrs ? [{ description: 'Dfrs', value: properties.dfrs }] : []
  }

  private addTargetIfExists(properties: WarningDataModel): any[] {
    return properties.target ? [{ description: 'Target', value: properties.target }] : []
  }

  /**
   * Adds location-specific details (H3 ID or Segment ID) only when they are present.
   * H3 ID is preferred for area warnings (hexagon-based), Segment ID for linear warnings.
   */
  private addWarningTypeSpecificDetails(items: any[], properties: WarningDataModel): void {
    // Add H3 ID if present (used for area/hexagon warnings like Low Visibility)
    if (properties.h3id) {
      items.push({ description: 'H3 ID', value: properties.h3id })
    }
    // Add Segment ID if present and H3 ID is not available (used for line warnings)
    else if (properties.segmentId) {
      items.push({ description: 'Segment ID', value: properties.segmentId })
    }
    // If neither is present, no location identifier will be shown
  }

  private createFeatureUniqueKey(feature: MapGeoJSONFeature): string {
    // Unique warning key used to keep the same warning on minute changes.
    const warningUUID = feature.properties?.['warningUUID'] || 'no-warning-uuid'
    const direction = feature.properties?.['direction'] || 'no-direction'
    return `${warningUUID}-${direction}`
  }

  // State Management
  private resetState(): void {
    this.selectedFeatureId = ''
    this.selectedFeatureIds = []
    this._details.set(new Map<string, DetailsModel>())
    this.rawFeature = undefined
  }

  private updateAppState(): void {
    this.appState.setState(AppPaths.Customer, {
      selectedFeatureId: this.selectedFeatureId || undefined,
    })
  }
}
